// @ts-strict-ignore
import {AttrsSchema, Component, ConfigOptions} from 'panel';
import produce from 'immer';

import {TrackingEvent} from 'common/report/dashboards/tracking';
import {useStore} from 'common/util/component-helpers';
import {userDetailsStore} from 'common/report/stores/user-details';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {FeatureRolloutName, FeatureRollouts} from 'common/report/util/feature-sampling';
import {
  ECommerceTemplateFields,
  TemplateField,
  TemplateSpecs,
  TemplateSpecsEntry,
  TemplateSpecsKeys,
  TemplateType,
  WebAnalyticsTemplateFields,
} from 'common/report/dashboards/types';
import {QueryContextRegistry} from 'common/query-builder/query-block/contexts/registry';
import {EventsController} from 'common/query-builder/query-block/contexts/events-controller';
import {fetchPropertyDefinitionsForEvents} from 'common/report/stores/property-definitions';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {getLocation} from 'common/util/url';

import {PAGE_LOADED_TOOLTIP, PAGE_NAME_TOOLTIP, template} from './template';
import css from './index.inline.styl';
import {BoardsTemplateModalAttrs, BoardsTemplateModalState, InstructionalTooltipConfig} from './types';

type JsxProps = {
  attrs: BoardsTemplateModalAttrs;
  on?: {
    apply?: (
      ev: CustomEvent<{
        fields: {[key in TemplateField]: any} | EmptyObj;
        projectId: string;
        selectedTemplate: TemplateType;
      }>,
    ) => void;
    trackEvent?: (
      ev: CustomEvent<{
        eventName: TrackingEvent;
        eventProperties: Obj;
      }>,
    ) => void;
    createBlueprint?: (
      ev: CustomEvent<{
        projectId: string;
        selectedTemplate: TemplateType;
      }>,
    ) => void;
  };
};

type TemplateConfigsEntry = Omit<TemplateSpecsEntry, `requiredFields`> & {
  requiredFieldsCheck: () => boolean;
  hasFeatureFlag: () => boolean;
};
type TemplateConfigsType = Record<TemplateSpecsKeys, TemplateConfigsEntry>;

export class BoardsTemplateModal extends Component<
  BoardsTemplateModalState,
  BoardsTemplateModalAttrs,
  any,
  any,
  QueryContextRegistry
> {
  declare template;

  static get attrsSchema(): AttrsSchema<BoardsTemplateModalAttrs> {
    return {
      ...super.attrsSchema,
      loading: {type: `boolean`},
    };
  }

  readonly TEMPLATE_SELECTION_STEP = 0;
  readonly EVENT_SELECTION_STEP = 1;
  readonly WHAT_TO_TRACK_URL = `https://developer.mixpanel.com/docs/plan-your-implementation#what-to-track`;
  readonly VALUE_MOMENT_FIELD_TOOLTIP_TEXT = `For a social media platform, it could be creating a post; for a streaming service, it's watching a video; for e-commerce, it might be completing a purchase. [Learn More](https://developer.mixpanel.com/docs/plan-your-implementation#what-to-track)`;

  /**
   * Used to filter out templates that require a feature flag to be displayed.
   * Will enable by default for dogfood projects.
   * @param featureRolloutName
   * @returns boolean
   */
  hasFeatureFlag(featureRolloutName: FeatureRolloutName | undefined): boolean {
    if (!featureRolloutName) {
      return true;
    }
    const currentProject = this.getContext(`currentProject`)?.get()?.value;
    if (!currentProject) {
      return false;
    }
    return FeatureRollouts[featureRolloutName].isEnabled(currentProject);
  }

  track(eventName: TrackingEvent, props?: {[key: string]: any}) {
    this.dispatchEvent(new CustomEvent(`trackEvent`, {detail: {eventName, eventProperties: props}}));
  }

  hasRequiredFields = (requiredFields: TemplateField[]): boolean => {
    return requiredFields.every((field) => !!this.state.fields[field]);
  };

  readonly TEMPLATE_CONFIGS: TemplateConfigsType = Object.keys(TemplateSpecs).reduce((acc, key: TemplateSpecsKeys) => {
    const {requiredFields, ...rest} = TemplateSpecs[key];
    const entry: TemplateConfigsEntry = {
      ...rest,
      requiredFieldsCheck: () => this.hasRequiredFields(requiredFields),
      hasFeatureFlag: () => this.hasFeatureFlag(rest.featureRolloutName),
    };
    return {
      ...acc,
      [key]: entry,
    };
  }, {} as TemplateConfigsType);

  ECOMMERCE_INSTRUCTIONAL_TOOLTIP_CONFIG: {[key in ECommerceTemplateFields]?: InstructionalTooltipConfig} = {
    [ECommerceTemplateFields.PURCHASE_EVENT]: {
      stepIndex: 1,
      title: `Select your Purchase event`,
      body: `This is the event that represents a completed customer order and payment transaction.`,
      shouldShow: () =>
        !this.state.fields[ECommerceTemplateFields.PURCHASE_EVENT] &&
        !this.elRefs[ECommerceTemplateFields.PURCHASE_EVENT]?.state.hasOpenDropdown,
    },
    [ECommerceTemplateFields.CART_PROPERTY]: {
      stepIndex: 2,
      title: `Select your Cart property`,
      body: `The Cart property is associated with your Purchase event and is a list of objects that represents the items a user has in their cart. \nThis property is often named Shopping cart, Cart, Bag, or something similar.`,
      shouldShow: () =>
        !!this.state.fields[ECommerceTemplateFields.PURCHASE_EVENT] &&
        !this.state.fields[ECommerceTemplateFields.CART_PROPERTY] &&
        !this.elRefs[ECommerceTemplateFields.CART_PROPERTY]?.state.isMenuOpen,
    },
    [ECommerceTemplateFields.PRICE_KEY]: {
      stepIndex: 3,
      title: `Select your Price property`,
      body: `The Price sub-property is associated with your Cart property and can be anything that represents how much an individual item costs.`,
      shouldShow: () =>
        !!this.state.fields[ECommerceTemplateFields.CART_PROPERTY] &&
        !this.state.fields[ECommerceTemplateFields.PRICE_KEY] &&
        !this.elRefs[ECommerceTemplateFields.PRICE_KEY]?.state.isMenuOpen,
    },
    [ECommerceTemplateFields.ADD_TO_CART_EVENT]: {
      stepIndex: 1,
      title: `Select your Add to Cart event`,
      body: `This is the event that represents a customer adding an item to their cart or shopping bag.`,
      shouldShow: () =>
        !!this.state.fields[ECommerceTemplateFields.PRICE_KEY] &&
        !this.state.fields[ECommerceTemplateFields.ADD_TO_CART_EVENT] &&
        !this.elRefs[ECommerceTemplateFields.ADD_TO_CART_EVENT]?.state.hasOpenDropdown,
    },
    [ECommerceTemplateFields.CART_PROPERTY_2]: {
      stepIndex: 2,
      title: `Select your Cart property`,
      body: `The Cart property is associated with your Purchase event and is a list of objects that represents the items a user has in their cart. \nThis property is often named Shopping cart, Cart, Bag, or something similar.`,
      shouldShow: () =>
        !!this.state.fields[ECommerceTemplateFields.ADD_TO_CART_EVENT] &&
        !this.state.fields[ECommerceTemplateFields.CART_PROPERTY_2] &&
        !this.elRefs[ECommerceTemplateFields.CART_PROPERTY_2]?.state.isMenuOpen,
    },
    [ECommerceTemplateFields.CATEGORY_KEY]: {
      stepIndex: 3,
      title: `Select your Category property`,
      body: `This sub-property is any descriptor about the objects in a user's cart such as Category, Brand, Item type, Style, etc. \nFor example, this property is used in a report that tells you what category has the most sales.`,
      shouldShow: () =>
        !!this.state.fields[ECommerceTemplateFields.CART_PROPERTY_2] &&
        !this.state.fields[ECommerceTemplateFields.CATEGORY_KEY] &&
        !this.elRefs[ECommerceTemplateFields.CATEGORY_KEY]?.state.isMenuOpen,
    },
  };

  WEB_ANALYTICS_INSTRUCTIONAL_TOOLTIP_CONFIG: {[key in WebAnalyticsTemplateFields]?: InstructionalTooltipConfig} = {
    [WebAnalyticsTemplateFields.PAGE_LOADED_EVENT]: {
      stepIndex: 1,
      title: `Select Page View Value`,
      body: PAGE_LOADED_TOOLTIP,
      shouldShow: () =>
        !this.state.fields[WebAnalyticsTemplateFields.PAGE_LOADED_EVENT] &&
        !this.elRefs[WebAnalyticsTemplateFields.PAGE_LOADED_EVENT]?.state.hasOpenDropdown,
    },
    [WebAnalyticsTemplateFields.PAGE_NAME_PROPERTY]: {
      stepIndex: 2,
      title: `Select Page Name Value`,
      body: PAGE_NAME_TOOLTIP,
      shouldShow: () =>
        !!this.state.fields[WebAnalyticsTemplateFields.PAGE_LOADED_EVENT] &&
        !this.state.fields[WebAnalyticsTemplateFields.PAGE_NAME_PROPERTY] &&
        !this.elRefs[WebAnalyticsTemplateFields.PAGE_NAME_PROPERTY]?.state.isMenuOpen,
    },
  };

  get config(): ConfigOptions<BoardsTemplateModalState, any, QueryContextRegistry> {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        open: false,
        selectedTemplate: null,
        fields: {},
        calendarInputOpen: false,
        loading: false,
      },
      contexts: [`currentProject` as const, `eventsController` as const, `i18n` as const],
      defaultContexts: {
        eventsController: new EventsController(),
      },
      helpers: {
        updateField: (value: any, eventName: TemplateField) => {
          this.update(
            produce(this.state, (draft) => {
              draft.fields[eventName] = value;
            }),
          );
        },
        removeField: (eventName: TemplateField) => {
          this.update(
            produce(this.state, (draft) => {
              delete draft.fields[eventName];
            }),
          );
        },
        resetState: () => this.update({fields: {}, calendarInputOpen: false}),
        shouldDisableCreateButton: (): boolean | undefined => {
          return !this.TEMPLATE_CONFIGS[this.state.selectedTemplate].requiredFieldsCheck() || this.attr(`loading`);
        },
        advanceToEventSelectionStep: () => {
          this.track(TrackingEvent.ViewedEventSelector, {'Selected Template': this.state.selectedTemplate});
          this.state.selectedTemplate && this.helpers.createBlueprintBoard();
        },
        createBlueprintBoard: (): void => {
          const {selectedTemplate} = this.state;
          const projectId = this.getContext(`currentProject`).id;
          this.dispatchEvent(new CustomEvent(`createBlueprint`, {detail: {projectId, selectedTemplate}}));
        },
      },
    };
  }

  elRefs = {};

  setElRef(ref, el) {
    this.elRefs[ref] = el;
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  attributeChangedCallback(name, oldValue, newValue): void {
    super.attributeChangedCallback(name, oldValue, newValue);
  }

  connectedCallback(): void {
    super.connectedCallback();
    const searchParams = new URL(getLocation().href).searchParams;
    const selectedTemplate = searchParams.get(`selected-template`);
    if (Object.values(TemplateType).includes(selectedTemplate as TemplateType)) {
      this.update({selectedTemplate: selectedTemplate as TemplateType});
    }
    useStore(this, userDetailsStore).fetch();
    fetchPropertyDefinitionsForEvents([], {});
    useStore(this, dataGroupStore).fetch();
  }
}

export const BoardsTemplateModalEl = defineAndMakeJsxFactory<JsxProps>(`boards-template-modal`, BoardsTemplateModal);
export const BoardsTemplateModalReact = panel2react<JsxProps>(`boards-template-modal`, BoardsTemplateModal);
